import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api';
import '../styles/NotificationBell.css';

const TYPE_CONFIG = {
  event_approved:         { icon: '✓', color: 'green', label: 'Event genehmigt' },
  event_rejected:         { icon: '✕', color: 'red',   label: 'Event abgelehnt' },
  verification_approved:  { icon: '★', color: 'blue',  label: 'Verifizierung genehmigt' },
  verification_rejected:  { icon: '✕', color: 'red',   label: 'Verifizierung abgelehnt' },
  new_event:              { icon: '!', color: 'blue',  label: 'Neues Event' },
  new_verification:       { icon: '!', color: 'blue',  label: 'Neue Verifizierungsanfrage' },
  event_revised:          { icon: '↺', color: 'blue',  label: 'Event überarbeitet' },
};

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  return `vor ${Math.floor(diff / 86400)} Tagen`;
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationService.getNotifications();
      setNotifications(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleMarkAllRead = async () => {
    await notificationService.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleItemClick = async (notification) => {
    if (!notification.isRead) {
      await notificationService.markRead(notification.id);
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
      );
    }
    if ((notification.type === 'event_approved' || notification.type === 'event_rejected') && notification.data?.eventId) {
      navigate(`/event/${notification.data.eventId}`);
      setOpen(false);
    } else if (notification.type === 'new_event' || notification.type === 'new_verification' || notification.type === 'event_revised') {
      navigate('/admin');
      setOpen(false);
    }
  };

  return (
    <div className="notification-bell-container" ref={ref}>
      <button
        className="notification-bell-btn"
        onClick={() => setOpen(o => !o)}
        title="Benachrichtigungen"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <span>Benachrichtigungen</span>
            {unreadCount > 0 && (
              <button className="mark-all-read-btn" onClick={handleMarkAllRead}>
                Alle gelesen
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">Keine Benachrichtigungen</div>
            ) : (
              notifications.map(n => {
                const config = TYPE_CONFIG[n.type] || { icon: '•', color: 'grey' };
                return (
                  <div
                    key={n.id}
                    className={`notification-item ${n.isRead ? '' : 'unread'}`}
                    onClick={() => handleItemClick(n)}
                  >
                    <span className={`notification-icon ${config.color}`}>{config.icon}</span>
                    <div className="notification-content">
                      <div className="notification-title">{n.title}</div>
                      <div className="notification-message">{n.message}</div>
                      <div className="notification-meta">
                        <span className="notification-time">{timeAgo(n.createdAt)}</span>
                        {(n.type === 'event_approved' || n.type === 'event_rejected') && n.data?.eventId && (
                          <span className="notification-action">Ansehen →</span>
                        )}
                        {(n.type === 'new_event' || n.type === 'new_verification' || n.type === 'event_revised') && (
                          <span className="notification-action">Admin →</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
